import { CarrierType, CompanyResponseDto, JourneyCategory } from '@planning/app/api'
import { FieldStore, FormStore, validateSubForm } from '@tom-ui/utils'
import { action, makeObservable } from 'mobx'
import { ContainerCreationFormData } from '../../components/StepperForm/stores/ContainerCreationFormStore'
import { SwapBodyCreationFormData } from '../../components/StepperForm/stores/SwapBodyCreationFormStore'
import { TrailerCreationFormData } from '../../components/StepperForm/stores/TrailerCreationFormStore'
import { DirectionalCargoDetailsType } from '../organisms/DirectionalCargoDetailsSubForm'
import { OrderNotesSubFormType } from '../organisms/OrderNotesSubForm'

export type CustomerSubFormType = {
  referenceNumber?: string
  consignee?: string | null
  customer?: CompanyResponseDto | null
  forwarder?: CompanyResponseDto | null
  shipper?: CompanyResponseDto | null

  forwarderId: number | null
  shipperId: number | null

  billOfLadding?: string
  dischargeBooking?: string
  loadBooking?: string
  shippingOrder?: string

  releaseReference?: string

  loadApproval: boolean
  dischargeApproval: boolean

  origin?: string
  inlandDestination?: string
  finalDestination?: string
  portOfLoading?: string
  portOfDischarge?: string

  journeyCategory?: JourneyCategory | null
}

export type OrderFormStoreType = CustomerSubFormType &
  OrderNotesSubFormType & {
    inboundCargoDetails?: DirectionalCargoDetailsType
    outboundCargoDetails?: DirectionalCargoDetailsType
    cargoDetails?: DirectionalCargoDetailsType
    unitInfo?: ContainerCreationFormData | SwapBodyCreationFormData | TrailerCreationFormData
  }

export class OrderFormStore extends FormStore<OrderFormStoreType> {
  referenceNumber: FieldStore<string>
  consignee: FieldStore<string>
  customer: FieldStore<CompanyResponseDto | null | undefined>

  billOfLadding: FieldStore<string>

  releaseReference: FieldStore<string>

  forwarder: FieldStore<CompanyResponseDto | null>
  shipper: FieldStore<CompanyResponseDto | null>

  forwarderId: FieldStore<number | null>
  shipperId: FieldStore<number | null>

  shippingOrder: FieldStore<string>
  dischargeApproval: FieldStore<boolean>
  loadApproval: FieldStore<boolean>

  origin: FieldStore<string>
  inlandDestination: FieldStore<string>
  finalDestination: FieldStore<string>
  portOfLoading: FieldStore<string>
  portOfDischarge: FieldStore<string>
  journeyCategory: FieldStore<JourneyCategory | null>

  constructor(initialValues?: Partial<OrderFormStoreType>) {
    super()

    makeObservable(this, {
      setData: action,
    })

    this.billOfLadding = new FieldStore(initialValues?.billOfLadding ?? '')
    this.releaseReference = new FieldStore(initialValues?.releaseReference ?? '')
    this.forwarder = new FieldStore<CompanyResponseDto | null>(initialValues?.forwarder ?? null)
    this.shipper = new FieldStore<CompanyResponseDto | null>(initialValues?.shipper ?? null)

    this.forwarderId = new FieldStore<number | null>(null)
    this.shipperId = new FieldStore<number | null>(null)

    this.shippingOrder = new FieldStore(initialValues?.shippingOrder ?? '')
    this.dischargeApproval = new FieldStore<boolean>(initialValues?.dischargeApproval ?? false)
    this.loadApproval = new FieldStore<boolean>(initialValues?.loadApproval ?? false)

    this.referenceNumber = new FieldStore(initialValues?.referenceNumber ?? '')
    this.consignee = new FieldStore(initialValues?.consignee ?? '')
    this.customer = new FieldStore<CompanyResponseDto | null | undefined>(
      initialValues?.customer,
      customer => {
        return customer?.id ? undefined : 'fieldIsRequired'
      },
    )

    this.origin = new FieldStore<string>(initialValues?.origin ?? '')
    this.inlandDestination = new FieldStore<string>(initialValues?.inlandDestination ?? '')
    this.finalDestination = new FieldStore<string>(initialValues?.finalDestination ?? '')
    this.portOfLoading = new FieldStore<string>(initialValues?.portOfLoading ?? '')
    this.portOfDischarge = new FieldStore<string>(initialValues?.portOfDischarge ?? '')
    this.journeyCategory = new FieldStore(initialValues?.journeyCategory ?? null)
  }

  validate = () => {
    return this.customer.validate() && validateSubForm(this.stores)
  }

  setData(data: Partial<OrderFormStoreType>) {
    this.referenceNumber.setValue(data.referenceNumber ?? this.referenceNumber.data)
    this.consignee.setValue(data.consignee ?? this.consignee.data)
    this.shippingOrder.setValue(data.shippingOrder ?? this.shippingOrder.data)

    this.billOfLadding.setValue(data.billOfLadding ?? this.billOfLadding.data)

    this.origin.setValue(data.origin ?? this.origin.data)
    this.inlandDestination.setValue(data.inlandDestination ?? this.inlandDestination.data)
    this.finalDestination.setValue(data.finalDestination ?? this.finalDestination.data)
    this.portOfLoading.setValue(data.portOfLoading ?? this.portOfLoading.data)
    this.portOfDischarge.setValue(data.portOfDischarge ?? this.portOfDischarge.data)
    this.releaseReference.setValue(data.releaseReference ?? this.releaseReference.data)

    if ('customer' in data) this.customer.setValue(data.customer ?? null)
    if ('forwarder' in data) this.forwarder.setValue(data.forwarder ?? null)
    if ('shipper' in data) this.shipper.setValue(data.shipper ?? null)

    if ('loadApproval' in data) this.loadApproval.setValue(!!data.loadApproval)
    if ('dischargeApproval' in data) this.dischargeApproval.setValue(!!data.dischargeApproval)

    if ('forwarderId' in data) this.forwarderId.setValue(data.forwarderId ?? null)
    if ('shipperId' in data) this.shipperId.setValue(data.shipperId ?? null)

    if ('journeyCategory' in data)
      this.journeyCategory.setValue(data.journeyCategory ?? this.journeyCategory.data ?? null)
  }

  get data(): OrderFormStoreType {
    return {
      ...super.data,

      referenceNumber: this.referenceNumber.data,
      consignee: this.consignee.data,
      shippingOrder: this.shippingOrder.data,

      billOfLadding: this.billOfLadding.data,

      customer: this.customer.data,
      forwarder: this.forwarder.data,
      shipper: this.shipper.data,

      forwarderId: this.forwarderId.data,
      shipperId: this.shipperId.data,

      dischargeApproval: this.dischargeApproval.data,
      loadApproval: this.loadApproval.data,

      origin: this.origin.data,
      inlandDestination: this.inlandDestination.data,
      finalDestination: this.finalDestination.data,
      portOfLoading: this.portOfLoading.data,
      portOfDischarge: this.portOfDischarge.data,
      releaseReference: this.releaseReference.data,
      journeyCategory: this.journeyCategory.data,
    }
  }

  get isRestow(): boolean {
    return (
      this.data?.inboundCargoDetails?.transport?.carrierType === CarrierType.Vessel &&
      this.data?.inboundCargoDetails?.transport?.carrierVisitId ===
        this.data?.outboundCargoDetails?.transport?.carrierVisitId
    )
  }
}
